import { comparisonData, competitorData } from '@/mocks/comparison';

export default function ComparisonSection() {
  return (
    <section className="py-16 md:py-24 bg-secondary-950">
      <div className="w-full px-4 md:px-6">
        <div className="mb-12 md:mb-16">
          <span className="inline-block px-4 py-1.5 text-xs font-semibold tracking-wider uppercase rounded-full bg-primary-500/20 text-primary-300 mb-4">
            Data-Driven Comparison
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-4 max-w-3xl">
            ACBuy Spreadsheet Product Rankings
          </h2>
          <p className="text-secondary-300 max-w-2xl text-sm md:text-base leading-relaxed">
            Real data from the ACBuy spreadsheet database. Compare best items, prices, ratings, and
            shipping estimates across every major category — updated daily for accuracy.
          </p>
        </div>

        <div className="overflow-x-auto mb-14">
          <table className="w-full min-w-[700px] text-left border-collapse">
            <thead>
              <tr className="border-b border-secondary-700">
                <th className="py-3.5 px-4 text-xs font-semibold text-secondary-400 uppercase tracking-wider">Category</th>
                <th className="py-3.5 px-4 text-xs font-semibold text-secondary-400 uppercase tracking-wider">Best Item</th>
                <th className="py-3.5 px-4 text-xs font-semibold text-secondary-400 uppercase tracking-wider">Avg Price</th>
                <th className="py-3.5 px-4 text-xs font-semibold text-secondary-400 uppercase tracking-wider">Rating</th>
                <th className="py-3.5 px-4 text-xs font-semibold text-secondary-400 uppercase tracking-wider">Shipping</th>
                <th className="py-3.5 px-4 text-xs font-semibold text-secondary-400 uppercase tracking-wider">Verdict</th>
              </tr>
            </thead>
            <tbody>
              {comparisonData.map((row, i) => (
                <tr
                  key={row.category}
                  className={`border-b border-secondary-800/50 hover:bg-secondary-800/30 transition-colors duration-150 ${
                    i % 2 === 0 ? 'bg-secondary-900/30' : ''
                  }`}
                >
                  <td className="py-3.5 px-4 text-sm font-medium text-background-50 whitespace-nowrap">
                    {row.category}
                  </td>
                  <td className="py-3.5 px-4 text-sm text-secondary-200">{row.bestItem}</td>
                  <td className="py-3.5 px-4 text-sm text-secondary-200 whitespace-nowrap">{row.avgPrice}</td>
                  <td className="py-3.5 px-4 text-sm">
                    <span className="inline-flex items-center gap-1 text-primary-300 font-semibold">
                      <i className="ri-star-fill text-xs"></i>
                      {row.rating}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 text-sm text-secondary-300 whitespace-nowrap">{row.shipping}</td>
                  <td className="py-3.5 px-4 text-sm">
                    <span className="inline-block px-2.5 py-1 text-xs font-semibold rounded-full bg-accent-500/20 text-accent-300 whitespace-nowrap">
                      {row.recommendation}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-2">
            ACBuy Spreadsheet vs Competitors
          </h3>
          <p className="text-secondary-300 text-sm mb-8 max-w-2xl leading-relaxed">
            Why the ACBuy spreadsheet comparison tool outperforms other platforms — cleaner data,
            faster updates, and a smarter filtering system that saves you time and money.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] text-left border-collapse">
              <thead>
                <tr className="border-b border-secondary-700">
                  <th className="py-3.5 px-4 text-xs font-semibold text-secondary-400 uppercase tracking-wider">Platform</th>
                  <th className="py-3.5 px-4 text-xs font-semibold text-secondary-400 uppercase tracking-wider">Data Quality</th>
                  <th className="py-3.5 px-4 text-xs font-semibold text-secondary-400 uppercase tracking-wider">Price Accuracy</th>
                  <th className="py-3.5 px-4 text-xs font-semibold text-secondary-400 uppercase tracking-wider">Update Speed</th>
                  <th className="py-3.5 px-4 text-xs font-semibold text-secondary-400 uppercase tracking-wider">Trust Score</th>
                </tr>
              </thead>
              <tbody>
                {competitorData.map((row, i) => (
                  <tr
                    key={row.platform}
                    className={`border-b border-secondary-800/50 ${
                      i === 0 ? 'bg-primary-500/10' : 'hover:bg-secondary-800/30'
                    } transition-colors duration-150`}
                  >
                    <td className="py-3.5 px-4 text-sm font-semibold text-background-50 whitespace-nowrap">
                      {row.platform}
                      {i === 0 && (
                        <span className="ml-2 text-xs text-primary-400">★ Top Pick</span>
                      )}
                    </td>
                    <td className="py-3.5 px-4 text-sm text-secondary-200">{row.dataQuality}</td>
                    <td className="py-3.5 px-4 text-sm text-secondary-200">{row.priceAccuracy}</td>
                    <td className="py-3.5 px-4 text-sm text-secondary-200">{row.updateSpeed}</td>
                    <td className="py-3.5 px-4 text-sm">
                      <span className="font-semibold text-primary-300">{row.trustScore}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-10 p-6 bg-primary-500/10 border border-primary-500/20 rounded-xl">
          <p className="text-sm text-secondary-200 leading-relaxed">
            <strong className="text-primary-300">Why ACBuy spreadsheet is better:</strong> Unlike
            PandaBuy lists or generic Taobao agent spreadsheets that may sit unupdated for weeks, the
            ACBuy item list refreshes daily. Its cleaner structure makes product discovery intuitive,
            and the AI-powered filtering system surfaces the best deals instantly — no manual sorting
            required. For anyone serious about finding verified, low-cost products efficiently, the
            ACBuy spreadsheet database is the clear winner in every measurable category.
          </p>
        </div>
      </div>
    </section>
  );
}